import type { JSX } from 'react';
import type { MasteryLevel } from '@yuan-math/learning';

/**
 * The mastery band badge, shared by every adventure surface that names a
 * concept: the kingdom's regions and skill tree, and the quest page's list of
 * what a quest is asking for.
 *
 * The pips are the reason this is not a colour scale. A band is a colour *and*
 * a count of filled pips *and* a word, because colour is never the only carrier
 * of meaning (CLAUDE.md, "A vivid colour never carries a word"). The count also
 * survives the thing a green ramp does not: told apart at a glance by someone
 * who cannot separate `--mastery-2` from `--mastery-3`.
 */

/** The five `masteryLevel` bands, said in words, plus how many pips they fill. */
export const BANDS: Record<MasteryLevel, { label: string; pips: number }> = {
  'not-started': { label: 'Not started', pips: 0 },
  attempted: { label: 'Attempted', pips: 1 },
  familiar: { label: 'Familiar', pips: 2 },
  proficient: { label: 'Proficient', pips: 3 },
  mastered: { label: 'Mastered', pips: 4 },
};

export const PIP_COUNT = 4;

/** Pips for the count, a word for the meaning. */
export function MasteryBand({ level }: { level: MasteryLevel }): JSX.Element {
  const { label, pips } = BANDS[level];
  return (
    <span className={`mastery-band mastery-band--${level}`}>
      <span className="mastery-band__pips" aria-hidden="true">
        {Array.from({ length: PIP_COUNT }, (_, index) => (
          <i key={index} className={`mastery-band__pip ${index < pips ? 'mastery-band__pip--on' : ''}`} />
        ))}
      </span>
      <span className="mastery-band__label">{label}</span>
    </span>
  );
}
